import { canadianProvinces, countriesAlpha2, usStates } from '@sequencehq/api'
import {
  Text,
  InputSelectControl,
  SettingsFormControl,
  TextInputField,
  Flex
} from '@sequencehq/core-components'
import { GreyGrey70, Primary13SemiBold } from '@sequencehq/core-components'
import { FC, ReactNode } from 'react'
import { Merchant, useMerchantSettings } from './useMerchantSettings'
import { BASE_INPUT_FIELD_STYLES } from 'SettingsV2/settings.constants'

type MerchantAddressFormControlProps = {
  label: string
  htmlFor: string
  children: ReactNode
}

const MerchantAddressFormControl: FC<MerchantAddressFormControlProps> = ({
  label,
  htmlFor,
  children
}) => {
  return (
    <Flex height="32px" alignItems="center" justifyContent="space-between">
      <Flex flexDirection="column" height="fit-content" gap="2px">
        <Text
          as="label"
          htmlFor={htmlFor}
          color={GreyGrey70}
          {...Primary13SemiBold}
        >
          {label}
        </Text>
      </Flex>

      {children}
    </Flex>
  )
}

export const MerchantAddressSettings = (props: { merchant: Merchant }) => {
  const { fields, getInputFieldProps, getSelectFieldProps } =
    useMerchantSettings(props.merchant)
  const postcodeLabel = fields.country.value === 'US' ? 'Zip' : 'Postal code'

  return (
    <>
      <SettingsFormControl label="Country" htmlFor="company-country">
        <InputSelectControl
          triggerStyle={{ width: BASE_INPUT_FIELD_STYLES.width }}
          options={Object.entries(countriesAlpha2).map(([key, label]) => ({
            value: key,
            label: label
          }))}
          {...getSelectFieldProps('country', {
            id: 'company-country',
            placeholder: 'Select country',
            label: 'Country'
          })}
        />
      </SettingsFormControl>
      {fields.country.value && (
        <Flex flexDirection="column" gap="16px" paddingY="16px">
          <MerchantAddressFormControl
            label="Address line 1"
            htmlFor="address-line-1"
          >
            <TextInputField
              {...BASE_INPUT_FIELD_STYLES}
              {...getInputFieldProps('line1', {
                id: 'address-line-1',
                placeholder: 'Address line 1',
                label: 'Line 1'
              })}
            />
          </MerchantAddressFormControl>

          <MerchantAddressFormControl
            label="Address line 2 (optional)"
            htmlFor="address-line-2"
          >
            <TextInputField
              {...BASE_INPUT_FIELD_STYLES}
              {...getInputFieldProps('line2', {
                id: 'address-line-2',
                placeholder: 'Address line 2',
                label: 'Line 2'
              })}
            />
          </MerchantAddressFormControl>

          <MerchantAddressFormControl label="City" htmlFor="city">
            <TextInputField
              {...BASE_INPUT_FIELD_STYLES}
              {...getInputFieldProps('town', {
                id: 'city',
                placeholder: 'City',
                label: 'City'
              })}
            />
          </MerchantAddressFormControl>

          {['US', 'CA'].includes(fields.country.value) && (
            <MerchantAddressFormControl label="State" htmlFor="state">
              <InputSelectControl
                triggerStyle={{ width: BASE_INPUT_FIELD_STYLES.width }}
                options={Object.entries(
                  fields.country.value === 'US' ? usStates : canadianProvinces
                ).map(([key, label]) => ({
                  value: key,
                  label: label
                }))}
                {...getSelectFieldProps('state', {
                  id: 'state',
                  label: 'State',
                  placeholder: 'Select state'
                })}
              />
            </MerchantAddressFormControl>
          )}

          <MerchantAddressFormControl
            label={postcodeLabel}
            htmlFor="postal-code"
          >
            <TextInputField
              {...BASE_INPUT_FIELD_STYLES}
              {...getInputFieldProps('postcode', {
                id: 'postal-code',
                placeholder: postcodeLabel,
                label: postcodeLabel
              })}
            />
          </MerchantAddressFormControl>
        </Flex>
      )}
    </>
  )
}
